import React, { useEffect } from "react";

import { useAnalytics } from "@common/analytics/useAnalytics";
import { ErrorStateView } from "@common/components/PageStateView/components/ErrorStateView";
import PageWrap from "@common/components/PageWrap/PageWrap";
import { BaseTable } from "@common/components/ShakeTable/BaseTable";
import { useTableInterjections } from "@common/components/ShakeTable/ui/interjectedRows/useTableInterjections";
import { Spinner } from "@common/components/Spinner/Spinner";
import { useUsersAttributes } from "@common/hooks/filtering/modules/useUsersAttributes";
import { Attributes } from "@common/hooks/filtering/ui/Attributes";
import { AttributesContainer } from "@common/hooks/filtering/ui/Attributes.styles";
import { useAppSelectionContext } from "@main/context/App/AppSelectionContext";
import { UsersModuleAddAppFooter } from "@main/pages/shared/components/AppFooter/AddAppFooter/AddAppFooter";
import { EmptyStateViewConf, EmptyTableView } from "@main/pages/shared/components/EmptyTableView/EmptyTableView";
import { UsersModuleEmptyState } from "@main/pages/shared/components/ModuleEmptyState/UserModuleEmptyState";
import { SdkSetupSteps } from "@main/pages/shared/components/SDKSetup/SdkSetupSteps";
import { resolveEmptyStateView } from "@main/pages/shared/helpers/resolveEmptyStateView";
import useUsersPageApiConsumer from "@main/pages/Users/consumers/useUsersPageApiConsumer";

export default function UsersPageView() {
  const { selectedApp } = useAppSelectionContext();
  const { sendEvent } = useAnalytics();

  useEffect(() => {
    sendEvent("page_view", { page_title: "Users list" });
  }, [sendEvent]);

  const usersAttributeState = useUsersAttributes();

  const { items, loading, error, userTableProps, refetchData } = useUsersPageApiConsumer({
    attributesProps: usersAttributeState,
  });

  const isInitialLoading = typeof items == "undefined" || loading;
  const hasAttributes = Boolean(usersAttributeState.validAttributes.length);

  const interjections = useTableInterjections("users", { hasActiveFilters: hasAttributes });
  const hasItems = Boolean(items && items.length > 0);

  if (!selectedApp?.is_approved) {
    return <SdkSetupSteps />;
  }

  if (!isInitialLoading && !hasAttributes && !error && !hasItems) return <UsersModuleEmptyState />;

  if (error) {
    return (
      <ErrorStateView
        title="There was an error loading users. Try refreshing."
        subtitle="The link you followed may be broken, or the workspace may have been removed."
      />
    );
  }

  const buildAppFooter = () => {
    if (selectedApp?.is_sample) return <UsersModuleAddAppFooter />;
    return null;
  };

  const emptyViewConfig = resolveEmptyStateView(
    loading,
    "users",
    hasItems,
    selectedApp,
    Boolean(usersAttributeState.validAttributes.length),
    Boolean(error),
    refetchData,
  ) as EmptyStateViewConf | undefined;

  return (
    <>
      <PageWrap style={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <AttributesContainer>
          <Attributes
            {...usersAttributeState}
            searchField="end_user_id"
            enableKeyboardShortcut={true}
          />
        </AttributesContainer>
        {emptyViewConfig && (
          <EmptyTableView
            withFilters
            {...emptyViewConfig}
          />
        )}
        {isInitialLoading && <Spinner />}
        {!isInitialLoading && !emptyViewConfig && (
          <BaseTable
            {...userTableProps}
            interjections={interjections}
            footer={buildAppFooter()}
            isFetchingNext={userTableProps.isFetchingNext}
          />
        )}
      </PageWrap>
    </>
  );
}
